/** その日の実際の天気（docs/04_world-and-weather.md 8 章）。プレイヤーにも CPU にも直接は見せない */
import type { DayProfile } from '../../config/weatherProfiles';
import { smoothstep } from '../../core/math';
import type { Rng } from '../../core/rng';
import type { Vec2 } from '../../core/vec';

export interface StormPlan {
  center: Vec2;
  onsetH: number;
  leadMin: number;
  lifeMin: number;
  radiusM: number;
}

export interface DayWeatherTruth {
  profileName: string;
  /** 0〜24 時の毎時の風（吹いてくる方位・ラジアン、2000m での風速 m/s） */
  windFromRad: number[];
  windMs2000: number[];
  thermal: { startH: number; peakH: number; endH: number; maxClimbMs: number };
  cloudBase: { startM: number; maxM: number };
  cumulusRatio: number;
  storms: StormPlan[];
}

/** 雷雨の起きやすい地点の候補（高い山頂） */
export function sampleTruth(profile: DayProfile, rng: Rng, stormSites: Vec2[]): DayWeatherTruth {
  const r = (range: readonly [number, number]): number => rng.range(range[0], range[1]);
  const baseFrom = (r(profile.wind.fromDeg) * Math.PI) / 180;
  const baseKmh = r(profile.wind.kmhAt2000m);
  const windFromRad: number[] = [];
  const windMs2000: number[] = [];
  let dir = baseFrom;
  let spd = baseKmh;
  for (let h = 0; h <= 24; h++) {
    // 1 時間ごとにゆっくり変わる（drift が大きい日ほど変わる）
    dir += rng.range(-1, 1) * profile.wind.drift * 0.35;
    spd += rng.range(-1, 1) * profile.wind.drift * 6;
    spd = Math.min(Math.max(spd, profile.wind.kmhAt2000m[0] * 0.6), profile.wind.kmhAt2000m[1] * 1.15);
    windFromRad.push(dir);
    windMs2000.push(spd / 3.6);
  }

  const storms: StormPlan[] = [];
  if (rng.chance(profile.storm.chance) && stormSites.length > 0) {
    const cells = rng.int(profile.storm.cells[0], profile.storm.cells[1]);
    const onset = r(profile.storm.onsetHour);
    for (let c = 0; c < cells; c++) {
      storms.push({
        center: rng.pick(stormSites),
        onsetH: onset + c * rng.range(0.3, 0.8),
        leadMin: r(profile.storm.leadMin),
        lifeMin: r(profile.storm.lifeMin),
        radiusM: rng.range(3000, 5000),
      });
    }
  }

  return {
    profileName: profile.name,
    windFromRad,
    windMs2000,
    thermal: {
      startH: r(profile.thermal.startHour),
      peakH: r(profile.thermal.peakHour),
      endH: r(profile.thermal.endHour),
      maxClimbMs: r(profile.thermal.maxClimbMs),
    },
    cloudBase: { startM: r(profile.cloudBase.startM), maxM: r(profile.cloudBase.maxM) },
    cumulusRatio: profile.cumulusRatio,
    storms,
  };
}

/** サーマルの強さの比率（0〜1） */
export function thermalRatio(tr: DayWeatherTruth['thermal'], timeOfDayS: number): number {
  const h = timeOfDayS / 3600;
  if (h <= tr.startH || h >= tr.endH) return 0;
  if (h < tr.peakH) return smoothstep(tr.startH, tr.peakH - 0.5, h);
  return 1 - smoothstep(tr.peakH + 1, tr.endH, h);
}

export function cloudBaseAt(truth: DayWeatherTruth, timeOfDayS: number): number {
  const h = timeOfDayS / 3600;
  const t = smoothstep(truth.thermal.startH, truth.thermal.peakH + 1, h);
  return truth.cloudBase.startM + (truth.cloudBase.maxM - truth.cloudBase.startM) * t;
}

/** 毎時の値を補間した風（to 方向のベクトル、2000m、m/s） */
export function baseWindAt(truth: DayWeatherTruth, timeOfDayS: number): { x: number; z: number } {
  const h = Math.min(Math.max(timeOfDayS / 3600, 0), 23.999);
  const i = Math.floor(h);
  const f = h - i;
  const vec = (k: number): [number, number] => {
    const from = truth.windFromRad[k] as number;
    const s = truth.windMs2000[k] as number;
    // 吹いてくる方位の反対向きに流れる
    return [-Math.sin(from) * s, Math.cos(from) * s];
  };
  const [x0, z0] = vec(i);
  const [x1, z1] = vec(i + 1);
  return { x: x0 + (x1 - x0) * f, z: z0 + (z1 - z0) * f };
}
